// Fase K4 — Selvbetjent avmelding fra åpen tur (/avmeld/:token). Engangs-token
// (samme mønster som /a/-alarmsvaret): ett klikk → bekreft → påmeldingen
// kanselleres og setet frigjøres atomisk. Vennlige tilstander for brukt/utløpt
// lenke og for tur som allerede er gjennomført.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertTriangle, CalendarDays, Ban } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePublicLanguage } from '@/components/public/usePublicLanguage';
import PublicLanguageToggle from '@/components/public/PublicLanguageToggle';

const API = process.env.REACT_APP_BACKEND_URL;

const STATE: Record<string, { title: [string, string]; body: [string, string]; icon: any }> = {
  used: { title: ['publicOpenTripCancelPage.alleredeAvmeldt', 'Allerede avmeldt'], body: ['publicOpenTripCancelPage.state.usedBody', 'Denne avmeldingslenken er allerede brukt.'], icon: Ban },
  expired: { title: ['publicOpenTripCancelPage.state.expiredTitle', 'Lenken har utløpt'], body: ['publicOpenTripCancelPage.state.expiredBody', 'Ta kontakt med operatøren hvis du fortsatt trenger å melde deg av.'], icon: AlertTriangle },
  completed: { title: ['publicOpenTripCancelPage.turenErGjennomfort', 'Turen er gjennomført'], body: ['publicOpenTripCancelPage.state.completedBody', 'Det er ikke lenger mulig å melde seg av.'], icon: CalendarDays },
};

export default function PublicOpenTripCancelPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<any>(null);
  const { lang, setPublicLanguage } = usePublicLanguage(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  const load = async () => {
    try {
      const res = await fetch(`${API}/api/public/open-trip/cancel/${token}`);
      if (!res.ok) throw new Error('bad');
      setData(await res.json());
    } catch { setErr(true); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const confirmCancel = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/public/open-trip/cancel/${token}`, { method: 'POST' });
      const j = await res.json();
      setDone(j?.state || 'cancelled');
    } catch { setErr(true); } finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  const company = data?.company || {};
  const Wrap = ({ children }: { children: React.ReactNode }) => (
    <div className="min-h-screen bg-slate-50 px-4 py-10">
      <div className="max-w-md mx-auto">
        <div className="flex items-center gap-3 mb-6">
          {company.logo ? <img src={company.logo} alt={company.name} className="h-8 w-auto max-w-[130px] object-contain" />
            : <div className="h-8 w-8 rounded-lg bg-emerald-600 text-white flex items-center justify-center font-bold text-sm">{(company.name || 'T')[0]}</div>}
          <span className="text-sm font-semibold text-slate-700">{company.name}</span>
          <div className="ml-auto"><PublicLanguageToggle lang={lang} onChange={setPublicLanguage} /></div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">{children}</div>
      </div>
    </div>
  );

  if (err || !data) {
    return <Wrap><AlertTriangle className="w-10 h-10 text-slate-300 mx-auto mb-3" /><h1 className="text-lg font-semibold text-slate-700">{t('publicOpenTripCancelPage.lenkenErIkkeGyldig', 'Lenken er ikke gyldig')}</h1></Wrap>;
  }

  if (done === 'cancelled') {
    return (
      <Wrap>
        <div data-testid="open-trip-cancel-done">
          <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-800">{t('publicOpenTripCancelPage.duErMeldtAv', 'Du er meldt av')}</h1>
          <p className="text-sm text-slate-500 mt-2">{t('publicOpenTripCancelPage.plassenDinErFrigjortTakk', 'Plassen din er frigjort. Takk for at du ga beskjed!')}</p>
        </div>
      </Wrap>
    );
  }
  if (done === 'completed') return <Wrap><CalendarDays className="w-10 h-10 text-slate-300 mx-auto mb-3" /><h1 className="text-lg font-semibold text-slate-700">{t('publicOpenTripCancelPage.turenErGjennomfort', 'Turen er gjennomført')}</h1></Wrap>;
  if (done === 'used') return <Wrap><Ban className="w-10 h-10 text-slate-300 mx-auto mb-3" /><h1 className="text-lg font-semibold text-slate-700">{t('publicOpenTripCancelPage.alleredeAvmeldt', 'Allerede avmeldt')}</h1></Wrap>;

  const info = STATE[data.state];
  if (info) {
    const Icon = info.icon;
    return <Wrap><div data-testid={`open-trip-cancel-state-${data.state}`}><Icon className="w-10 h-10 text-slate-300 mx-auto mb-3" /><h1 className="text-lg font-semibold text-slate-700">{t(...info.title)}</h1><p className="text-sm text-slate-500 mt-2">{t(...info.body)}</p></div></Wrap>;
  }

  // state === 'valid'
  return (
    <Wrap>
      <div data-testid="open-trip-cancel-confirm">
        <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
        <h1 className="text-xl font-bold text-slate-800">{t('publicOpenTripCancelPage.meldeDegAv', 'Melde deg av?')}</h1>
        <p className="text-sm text-slate-600 mt-2">
          {t('publicOpenTripCancelPage.vilDuMeldeDegAv', 'Vil du melde deg av')} <strong>«{data.title}»</strong>{data.seats ? ` (${t('publicOpenTripCancelPage.nSeter', '{{count}} sete', { count: data.seats })})` : ''}?
        </p>
        <div className="mt-6 flex gap-2">
          <button onClick={() => window.history.length > 1 ? window.history.back() : window.close()} data-testid="open-trip-cancel-keep"
            className="flex-1 rounded-lg border border-slate-300 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50">{t('publicOpenTripCancelPage.beholdPlassen', 'Behold plassen')}</button>
          <button onClick={confirmCancel} disabled={busy} data-testid="open-trip-cancel-confirm-btn"
            className="flex-1 rounded-lg bg-red-600 hover:bg-red-700 text-white py-2.5 text-sm font-semibold disabled:opacity-60 flex items-center justify-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t('publicOpenTripCancelPage.jaMeldMegAv', 'Ja, meld meg av')}
          </button>
        </div>
      </div>
    </Wrap>
  );
}
